///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GtthAlertService } from '../../../services/alert.service';
import { GtthEventManager } from '../../../services/event-manager.service';
import { GtthPreloaderModalEvent } from '../../../shared/preloader-modal/preloader-modal-event';
import { GtthEntityNameValidation } from '../../../shared/directives/validation-functions/entity-name';
import { parseFormDataToObj } from '../../../shared/utils';
import { Subject } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ProjectFolderDTO, ProjectFolderEnum } from '../../../generated/shared-types.model';
import { FoldersService } from '../../../services/project/folders.service';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-folder-dialog',
    templateUrl: './folder-dialog.component.html',
    standalone: false
})
export class GtthFolderDialogComponent implements OnInit, OnDestroy {
    editForm: FormGroup;

    projectFolder: ProjectFolderDTO;
    isSaving: boolean;
    showFormValidationAlert: boolean;
    alertMessageSubject: Subject<string>;

    constructor(
        private readonly alertService: GtthAlertService,
        private readonly projectFolderService: FoldersService,
        private readonly eventManager: GtthEventManager,
        private readonly formBuilder: FormBuilder,
        public dialogRef: MatDialogRef<GtthFolderDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { projectFolder: ProjectFolderDTO }
    ) {}

    @HostListener('document:keydown', ['$event'])
    handleKeyboardUpEvent(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.save();
        }
    }

    ngOnInit() {
        this.isSaving = false;
        this.projectFolder = this.data.projectFolder;
        this.initForm();
    }

    ngOnDestroy() {
        if (this.alertMessageSubject) {
            this.alertMessageSubject.complete();
        }
    }

    initForm() {
        this.alertMessageSubject = new Subject();
        this.editForm = this.formBuilder.group({
            name: [
                this.projectFolder.name,
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(250),
                    GtthEntityNameValidation(/[^A-Za-z\d\s#@!;$&%()_+=.,~\-{}[\]^'`]/g)
                ])
            ]
        });
    }

    save() {
        if (this.isSaving) {
            return;
        }

        if (this.editForm.invalid) {
            this.alertMessageSubject.next('error.formRequiredFields');
            return;
        }
        this.alertMessageSubject.next('');

        parseFormDataToObj(this.projectFolder, this.editForm);

        this.isSaving = true;
        this.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.show('Saving')
        });

        if (isDefined(this.projectFolder.id)) {
            this.projectFolderService.updateFolder(this.projectFolder).subscribe({
                next: () => this.onSaveSuccess(),
                error: (err) => this.onSaveError(err)
            });
        } else {
            this.projectFolderService.createFolder(this.projectFolder).subscribe({
                next: () => this.onSaveSuccess(),
                error: (err) => this.onSaveError(err)
            });
        }
    }

    private onSaveSuccess() {
        this.eventManager.broadcast({ name: 'folderModified', content: 'OK' });
        this.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.hide()
        });

        this.isSaving = false;
        this.dialogRef.close();
    }

    private onSaveError(error) {
        try {
            error.json();
        } catch (exception) {
            error.message = error.text();
            console.log(`Failed to save - ${exception}`);
        }
        this.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.hide()
        });
        this.isSaving = false;
        this.alertService.error(error.message, null, null);
    }
}

@Component({
    selector: 'winde-folder-popup',
    template: '',
    standalone: false
})
export class GtthFolderPopupComponent implements OnInit {
    constructor(
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dialog: MatDialog,
        private readonly projectFolderService: FoldersService
    ) {}

    ngOnInit() {
        const params = this.route.snapshot.paramMap;
        const id = params.has('id') ? parseInt(params.get('id'), 10) : undefined;

        if (isDefined(id)) {
            this.projectFolderService.getFolder(id).subscribe((folderDto: ProjectFolderDTO) => {
                if (!folderDto) {
                    this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
                } else {
                    const dialogRef = this.dialog.open(GtthFolderDialogComponent, {
                        width: '750px',
                        position: {
                            top: '30px'
                        },
                        data: {
                            projectFolder: folderDto
                        },
                        closeOnNavigation: true
                    });

                    dialogRef.afterClosed().subscribe(() => {
                        this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
                    });
                }
            });
        } else {
            const rootFolderIds = this.route.snapshot.data.rootFolderIds;
            const customerId = params.has('customerId') ? parseInt(params.get('customerId'), 10) : undefined;
            const parentId = params.has('parentId') ? parseInt(params.get('parentId'), 10) : rootFolderIds[ProjectFolderEnum.PROJECT];

            const projectFolder = {
                id: undefined,
                name: '',
                customerId,
                parentId
            } as ProjectFolderDTO;

            const dialogRef = this.dialog.open(GtthFolderDialogComponent, {
                width: '750px',
                position: {
                    top: '30px'
                },
                data: {
                    projectFolder
                },
                closeOnNavigation: true
            });

            dialogRef.afterClosed().subscribe(() => {
                this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
            });
        }
    }
}
